/**
 * Authored habitat bases — set dressing, not player construction.
 *
 * `docs/HABITAT.md` §3 is explicit that there is no placement system, no build
 * mode and no blueprint anywhere in `src/`, and that a player-driven modular
 * construction system is a larger feature than anything on `NEXT.md`'s list. So
 * this is Phase 0: compositions authored by hand, placed in the world, drawn
 * with no new systems at all.
 *
 * The point of having them now, before any mechanics, is §10 — the success test
 * is a question about pixels ("can you tell what kind of module it is from the
 * silhouette alone, at 63 px?") and it cannot be answered in CAD. It has to be
 * answered in the water, at gameplay framing, with the lamp on.
 *
 * §12's five-stage roadmap is a LEVEL DESIGN progression, not a build menu: the
 * player travels through it west to east rather than assembling it. These four
 * are the stages, laid out so they can be flown between and compared.
 */

import { HAB } from '../render/habitat';

/** One placed module. `rot` is about Z, which keeps it in the play plane (§7). */
export interface HabPart {
  mesh: string;
  /** Offset from the base origin, in metres. x runs along the world, y is up. */
  x: number;
  y: number;
  rot?: number;
  /**
   * Vertical scale only. Legs use it: modules share the passage centreline but
   * have different keels, so a leg has to be cut to length to reach the same
   * ground as its neighbours. Y alone, because a uniform scale would make a
   * longer leg a fatter one too.
   */
  sy?: number;
  /** Per-instance tint; §6.5 assigns these by ROLE, not by taste. */
  tint?: [number, number, number, number];
  emis?: [number, number, number, number];
  /**
   * PBR override: metallic, roughness, dielectric F0, enable (§2.3).
   *
   * Without this an instance shades as "whatever slot 0.0 means", and MAT.paint
   * is a lacquered dielectric with a clear coat TUNED FOR THE BOAT. A habitat is
   * not the boat: the target sheet asks for "higher quality PBR materials,
   * subtle metal roughness variation", and this is the hook that exists for it.
   */
  pbr?: [number, number, number, number];
}

export interface HabBase {
  id: string;
  name: string;
  /** World x of the base origin. Y is taken from the seabed at run time. */
  x: number;
  /** Nudge the whole base up or down off the seabed. */
  lift: number;
  parts: HabPart[];
}

// §6.5: findable modules take a pale body so they catch the lamp; industrial
// modules take the render's graphite and sit behind as mass; structure is
// charcoal throughout. Cyan is the accent that SURVIVES — extinction kills red
// first, so an amber strip at −2500 m is a grey strip. Amber means heat, and
// belongs to the power module and the refinery only.
/**
 * §13 QUESTION 6, ANSWERED: graphite, not pale.
 *
 * §6.5 proposes a role split — findable modules pale so they catch the lamp,
 * industrial modules the render's graphite — and is explicit that this "is a
 * recommendation, not a measurement", to "be judged on a real frame with the
 * lamp on before it is settled".
 *
 * Judged. Pale loses, and not narrowly. At 0.54 a body is brighter than the
 * emissive strips ON it, so the accents have nothing to sit against and the
 * whole module flattens into one value — which is what "still looks flat" was.
 * The concept sheet and the revised target are both near-black bodies with
 * bright cyan; the contrast IS the look, and it only exists if the body is dark.
 *
 * §6.5's worry — "a graphite body at -2500 m is invisible except for its
 * emissives" — turns out to be the FEATURE. The emissives are the landmark, and
 * they read from much further off than a grey shape does.
 */
/**
 * ALBEDO, IN THE RANGE A SURFACE CAN ACTUALLY HAVE.
 *
 * These were 1.06 and 0.88 — a hull reflecting 106 percent of the light that
 * reaches it, and something called "graphite" brighter than fresh snow. They
 * were set when a module was a flat-shaded box and the tint had to carry all of
 * its value on its own; with CAD geometry, real material slots and a searchlight
 * that reaches, the same numbers saturate the diffuse term outright and bloom
 * turns a module into a white blob with a halo. No specular dial can help,
 * because the blowout is not in the specular.
 *
 * 0.78 is a clean painted hull, near the top of what paint reaches. 0.34 is
 * graphite, which is a dark grey — the whole point of the two-tone kit is that
 * one is light and the other is not.
 */
const PALE: [number, number, number, number] = [0.78, 0.80, 0.82, 1];
const GRAPHITE: [number, number, number, number] = [0.34, 0.37, 0.42, 1];
const CYAN: [number, number, number, number] = [0.3, 2.0, 2.6, 1];
const AMBER: [number, number, number, number] = [2.4, 1.25, 0.4, 1];
const DIM: [number, number, number, number] = [0.1, 0.5, 0.65, 1];

// Metal, and rougher than the boat's lacquer: a habitat has been down here for
// years. Roughness carries most of the difference between "machined" and "toy".
// A conductor's whole visible response is its SPECULAR LOBE — `diffAlbedo` is
// `albedo * (1 - metallic)`, so a metal has almost no Lambert term and is only
// as visible as what it has to reflect. Down here that is the player's lamp,
// and a lamp only reads as a highlight on a SHARP surface: at roughness 0.38 the
// lobe is smeared over the hemisphere and comes back as flat grey. 0.22 gives it
// somewhere to land.
const PBR_METAL: [number, number, number, number] = [0.9, 0.22, 0.04, 1];
const PBR_HULL: [number, number, number, number] = [0.08, 0.42, 0.05, 1];

/** Four legs under a module of `len`, from its keel at `y − hy`. */
/**
 * WHERE EACH MODULE'S KEEL ACTUALLY IS, measured off the Fusion CAD.
 *
 * Legs attach here and the vertical stack hangs from here, and until now both
 * used two constants — `HY_MOD` 3.0 and `HY_CORE` 3.75 — inherited from the
 * procedural meshes. Every module in the CAD disagrees with them, and
 * `hab_obs` disagrees by 3.15 m: its dome sits almost entirely ABOVE its
 * origin, bottoming out at -0.45 rather than -3.6. That is why the legs float,
 * the dock hangs at the wrong distance, and nothing lines up.
 *
 * Same reasoning as `HAB.LEN`: sec.2.4 makes Fusion the dimension authority,
 * so these are read off the kit rather than guessed. Re-run
 * `bake-habitat.mjs`, which prints every module's bounds, if the CAD moves.
 */
const BOUNDS: Record<string, [number, number]> = {
  hab_core: [-3.45, 4.75], hab_power: [-2.50, 3.00], hab_store: [-2.50, 2.50],
  hab_refine: [-2.50, 5.30], hab_extract: [-2.50, 2.50], hab_dock: [-4.30, 2.95],
  hab_green: [-2.20, 2.20], hab_obs: [-0.45, 3.75], hab_tube: [-1.60, 1.60],
  hab_tee: [-2.00, 2.45], hab_elbow: [-2.00, 2.45], hab_cross: [-2.45, 2.45],
  hab_leg: [-1.10, 0.00], hab_lo: [-3.00, 3.00],
};

/** A module's keel, or the caller's fallback when the name is unknown. */
function keel(mesh: string, fallback: number): number {
  return BOUNDS[mesh]?.[0] ?? -fallback;
}

/** A module's crown. */
function crown(mesh: string, fallback: number): number {
  return BOUNDS[mesh]?.[1] ?? fallback;
}

/** The leg mesh's own length: it spans -1.10 to 0 about its mounting point. */
const LEG_LEN = 1.10;

/**
 * WHERE EVERY FOOT IN A RUN LANDS. The deepest keel in the kit is the core's
 * -3.45, one leg below which is -4.55.
 */
const FOOT = -4.55;

/**
 * Legs, CUT TO LENGTH, which is the only way they can line up.
 *
 * Modules have to share the passage centreline at y = 0 or they cannot connect,
 * and the CAD gives them different depths — the core reaches -3.45, the
 * greenhouse only -2.20. A leg of one fixed length hung from each keel
 * therefore ends somewhere different under every module: measured across a
 * stage-5 run the feet spread over 1.25 m, which is the "legs all over the
 * place".
 *
 * They cannot all be the same length AND all reach the ground, so they are
 * scaled. `compose` already takes a non-uniform scale, and stretching Y alone
 * keeps the foot the same size as its neighbours — a leg scaled uniformly would
 * come out fatter as well as longer, which is worse than the problem.
 */
function legs(x: number, len: number, y: number, bottom: number): HabPart[] {
  const inset = Math.min(1.6, len * 0.2);
  const sy = Math.max(0.35, (y + bottom - FOOT) / LEG_LEN);
  return [x + inset, x + len - inset].map((lx) => (
    { mesh: 'hab_leg', x: lx, y: y + bottom, sy, tint: GRAPHITE, emis: DIM, pbr: PBR_METAL } as HabPart
  ));
}

const L = HAB.LEN;

/** Lay modules end to end along X, so a layout cannot drift from the meshes. */
function run(x0: number, items: [string, number, number, [number, number, number, number]][]): HabPart[] {
  const out: HabPart[] = [];
  let x = x0;
  for (const [mesh, len, hy, emis] of items) {
    const tint = mesh === 'hab_core' || mesh === 'hab_dock' || mesh === 'hab_obs'
      || mesh === 'hab_green' ? PALE : GRAPHITE;
    out.push({ mesh, x, y: 0, tint, emis, pbr: mesh === 'hab_tube' ? PBR_METAL : PBR_HULL });
    if (mesh !== 'hab_tube') out.push(...legs(x, len, 0, keel(mesh, hy)));
    x += len;
  }
  return out;
}

const HY_MOD = 3.0, HY_CORE = 3.75;
const TUBE: [string, number, number, [number, number, number, number]] = ['hab_tube', L.tube, 1.6, CYAN];

/** §12 stage 1: a camp, not a base. */
function stage1(): HabPart[] {
  return run(0, [
    ['hab_extract', L.extract, HY_MOD, DIM], TUBE,
    ['hab_power', L.power, HY_MOD, AMBER],
  ]);
}

/**
 * §12's first build target, corrected to two dimensions and to this world:
 *
 *   EXTRACTOR — tube — POWER — tube — HABITAT CORE
 *                                          |  vertical shaft
 *                                    AIRLOCK / DOCK
 *
 * The dock hangs BELOW: the player flies in low and under, which frames the base
 * against open water above rather than against rock (§0.5).
 */
function stage2(): HabPart[] {
  const p = run(0, [
    ['hab_extract', L.extract, HY_MOD, DIM], TUBE,
    ['hab_power', L.power, HY_MOD, AMBER], TUBE,
    ['hab_core', L.core, HY_CORE, CYAN],
  ]);
  const coreX = L.extract + L.tube + L.power + L.tube;
  const mid = coreX + L.core / 2;
  // The vertical drop. A tube rotated 90° about Z is catalog part 13 (§7).
  /**
   * THE VERTICAL DROP, MEASURED RATHER THAN GUESSED.
   *
   * A tube rotated 90 degrees about Z sends its local +X to world +Y, so it
   * grows UPWARD from its origin. Placing that origin at the core's keel put
   * the whole shaft inside the core; the dock then hung from a constant that
   * did not match either body, leaving the gap visible under the core.
   *
   * Written as a chain instead: the shaft's crown meets the core's keel, and
   * the dock's crown meets the shaft's keel. Every term is a real bound.
   */
  const coreKeel = keel('hab_core', HY_CORE);
  const shaftY = coreKeel - L.tube;
  p.push({ mesh: 'hab_tube', x: mid, y: shaftY, rot: Math.PI / 2, tint: GRAPHITE, emis: CYAN, pbr: PBR_METAL });
  p.push({ mesh: 'hab_dock', x: mid - L.dock / 2, y: shaftY - crown('hab_dock', HY_MOD),
    tint: PALE, emis: CYAN, pbr: PBR_HULL });
  return p;
}

/** §12 stage 3: resource flow. Refinery and storage join the run. */
function stage3(): HabPart[] {
  const p = stage2();
  const end = L.extract + L.tube + L.power + L.tube + L.core;
  p.push(...run(end, [
    TUBE, ['hab_refine', L.refine, HY_MOD, AMBER],
    TUBE, ['hab_store', L.store, HY_MOD, DIM],
  ]));
  return p;
}

/**
 * §12 stage 5: an advanced facility, and deliberately over the 16 000 budget.
 * 16 000 is the budget for a TYPICAL base; one or two authored complexes at more
 * are affordable in frames that can carry them. Check the frame before placing
 * one — and do not put the second one in the kelp grotto.
 */
function stage5(): HabPart[] {
  const p = stage3();
  const coreX = L.extract + L.tube + L.power + L.tube;
  const mid = coreX + L.core / 2;
  // A tee on the core's crown, carrying the dome clear of the roof line.
  /**
   * THE TOWER, CHAINED THE SAME WAY. Each body's keel meets the crown of the
   * one below it, so nothing is a guess and nothing floats.
   *
   * `HY_CORE + 1.2` and the 2.4 after it were offsets tuned against the
   * procedural core, whose crown was 3.75. The CAD core reaches 4.75 — it
   * carries its own mounting tower — so the whole stack sat over a metre low
   * and buried its first joint.
   */
  const teeY = crown('hab_core', HY_CORE) - keel('hab_tee', 2.4);
  const mastY = teeY + crown('hab_tee', 2.4);
  p.push({ mesh: 'hab_tee', x: mid - L.node / 2, y: teeY, tint: GRAPHITE, emis: CYAN, pbr: PBR_METAL });
  p.push({ mesh: 'hab_tube', x: mid, y: mastY, rot: Math.PI / 2, tint: GRAPHITE, emis: CYAN, pbr: PBR_METAL });
  /**
   * CENTRED ON THE MAST, which it was not.
   *
   * Every other module in the kit is laid out by `run()` with its origin at its
   * LEFT end and grows +X from there, so `x: mid` puts a body's left edge on the
   * mast rather than its middle. The dome is 6.10 m long, so it hung 3.05 m off
   * to one side of the tower holding it up.
   *
   * The tee and the dock already subtract half their length for exactly this;
   * the dome was the one that did not. Its own mounting collar sits at x 3.025
   * of its 6.10 span — the CAD centres it — so half the length is the right
   * correction rather than a fudge.
   */
  p.push({ mesh: 'hab_obs', x: mid - L.obs / 2, y: mastY + L.tube - keel('hab_obs', 3.6),
    tint: PALE, emis: CYAN, pbr: PBR_HULL });
  const end = coreX + L.core + L.tube + L.refine + L.tube + L.store;
  p.push(...run(end, [TUBE, ['hab_green', L.green, 2.55, [1.7, 1.15, 0.55, 1]]]));
  return p;
}

/**
 * Where the test bases sit. §12 names `s-shelf` (x 150) as the obvious first
 * candidate for the first build target — shallow, lit, early, and near enough to
 * spawn that it gets looked at on every single run.
 *
 * The other three are spaced far enough apart to be judged on their own, and all
 * four are clear of the two frames §2.6 says not to stack a base on top of: the
 * kelp-forest grotto at x ≈ 3090 and the densest bait-ball chunks.
 */
export const TEST_BASES: HabBase[] = [
  // §12 names `s-shelf` (x 150) as the obvious first candidate: shallow, lit,
  // early. SHALLOW IS NOT INCIDENTAL — the IBL reflection that makes a metal
  // read as metal is scaled by the ambient level (`sky = G.fog.w` in fsMesh), so
  // it is "full on the shelf, two thirds of a percent in the abyss". A base
  // judged at 125 m in the cave system is being judged where the engine has
  // deliberately switched off the effect the look depends on.
  { id: 'hb-camp', name: 'STAGE 1 · SURVIVAL CAMP', x: 60, lift: 4.0, parts: stage1() },
  { id: 'hb-shelf', name: 'STAGE 2 · FIRST HABITAT', x: 210, lift: 4.6, parts: stage2() },
  // §2.6's real rule is not "keep bases out of cheap frames", it is DO NOT STACK
  // A BASE ON TOP OF A FRAME THAT IS ALREADY HEAVY. The cave system at x 1290+
  // measured 207.7 k triangles with the stage-5 complex in it — past the 204 k
  // kelp-forest grotto, which is the heaviest frame in the game. Both of the big
  // compositions moved west into open water.
  { id: 'hb-flow', name: 'STAGE 3 · RESOURCE FLOW', x: 430, lift: 4.6, parts: stage3() },
  { id: 'hb-complex', name: 'STAGE 5 · ADVANCED FACILITY', x: 880, lift: 4.6, parts: stage5() },
];
